import {
  BlogPublicHub,
  BlogPublicPost,
  blogListingHref,
  blogReadingMinutes,
  SpringPage,
} from '@/lib/entities/blog';

import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

import styles from './BlogHubPage.module.scss';
import { ArticleCard } from './components/ArticleCard';
import { BlogHubChrome } from './components/BlogHubChrome';
import { BlogPagination } from './components/BlogPagination';

const FeaturedPost = dynamic(() => import('./components/FeaturedPost'), {
  ssr: true,
});

type BlogHubPageProps = {
  hub: BlogPublicHub;
  latest: SpringPage<BlogPublicPost>;
  uiPage: number;
  locale: string;
  title?: string;
};

export const BlogHubPage = ({
  hub,
  latest,
  uiPage,
  locale,
  title,
}: BlogHubPageProps) => {
  const { t } = useTranslation('index', { keyPrefix: 'blog' });
  const showFeatured = uiPage === 1 && hub.popular.length > 0;
  const latestPosts = latest.content;
  const totalPages = latest.totalPages;

  return (
    <div className={styles.hub}>
      <div className={styles.hub__container}>
        <BlogHubChrome
          locale={locale}
          categories={hub.categories}
          tags={hub.tags}
          title={title || t('title')}
          subtitle={t('subtitle')}
        />

        {showFeatured ? (
          <FeaturedPost posts={hub.popular} locale={locale} />
        ) : null}

        {latestPosts.length > 0 ? (
          <section
            id="blog-latest"
            className={styles.hub__section}
            aria-labelledby="blog-latest-title"
          >
            <h2 id="blog-latest-title" className={styles.hub__sectionTitle}>
              {t('latest')}
            </h2>
            <div className={styles.hub__grid}>
              {latestPosts.map((post) => (
                <ArticleCard
                  key={post.id}
                  post={post}
                  locale={locale}
                  readingLabel={t('readingTimeFull', {
                    count: blogReadingMinutes(post),
                  })}
                />
              ))}
            </div>
            <BlogPagination
              currentPage={uiPage}
              maxPages={totalPages}
              hrefForPage={(page) => blogListingHref('/blog', page)}
            />
          </section>
        ) : null}

        {latest.totalElements === 0 && !showFeatured ? (
          <p className={styles.hub__empty}>{t('empty')}</p>
        ) : null}
      </div>
    </div>
  );
};
